import {
  ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output,
} from '@angular/core';
import {
  FormBuilder, FormControl, FormGroup, Validators,
} from '@angular/forms';
import {
  UIKitIconRegistry, calculatorIcon, profileIcon, infoIcon, chevronLeftIcon, startOverIcon, calendarIcon, gbpIcon, plusIcon, minusIcon, chevronRightIcon, RestApiService, chevronDownIcon, chevronUpIcon, homeIcon, LoggerService,
} from '@ui-kit';
import { P } from '@ui-kit/ui-kit';
import { calculateBudget, recommendation } from 'calcloan';
import { debounceTime, startWith } from 'rxjs/operators';
import * as moment from 'moment';
import { CurrencyPipe } from '@angular/common';
import { tooltipFormat } from '../../shared.helpers';
import { UserService } from '../../service/user.service';
import { LeanFavourite } from '../../../public/favourites/shortlist.helpers';

export type AdvancedCalculatorStep = 'Budget' | 'Breakdown' | 'Repayment';

export const defaultOptions = {
  label: 'label',
  start: 500000,
  range: { min: 100000, max: 1200000 },
  step: 1000,
  connect: false,
  behaviour: 'tap-drag',
  // tooltips: [tooltipFormat, tooltipFormat],
  displayFormat: tooltipFormat.to,
};

@Component({
  selector: 'app-advanced-calculator',
  templateUrl: './advanced-calculator.component.html',
  styleUrls: ['./advanced-calculator.component.scss'],
  providers: [CurrencyPipe],
})
export class AdvancedCalculatorComponent implements OnInit {
  @Input() data: {} | undefined;

  @Input() listing: P.ListingExtended | undefined;

  @Input() set favourite(favourite: LeanFavourite | undefined) {
    this.listing = favourite?.price
      ? {
        metadata: {
          price: favourite?.price || this.user.budget,
        },
      } as P.ListingExtended
      : undefined;

    // this.cdr.detectChanges();
  }

  @Output() event: EventEmitter<Event> = new EventEmitter();

  step: AdvancedCalculatorStep = 'Budget';

  budgetFocus: 0 | 1 = 0;

  budgetStore: {
    applicants: any[];
    application: any;
  };

  budgetScenario: Calculator.Scenario | undefined;

  budgetScenarioWarns: (Calculator.Message | Calculator.Deduction)[] = [];

  budgetGroup: FormGroup = this.formBuilder
    .group({
      // applicant in focus
      residence_type: [
        (<any> this.user.current.application.applicants[this.budgetFocus]).residence_type || '',
        Validators.compose([
          Validators.required,
        ]),
      ],
      employment_type: [
        (<any> this.user.current.application.applicants[this.budgetFocus]).employment_type || '',
        Validators.compose([
          Validators.required,
        ]),
      ],
      self_employment_type: [
        (<any> this.user.current.application.applicants[this.budgetFocus]).self_employment_type || '',
        Validators.compose([]),
      ],
      dob: [
        (<any> this.user.current.application.applicants[this.budgetFocus]).dob || '',
        Validators.compose([(control: FormControl) => {
          const value = moment(control.value);
          if (value > moment().subtract(17, 'years')) {
            return { dateInvalid: true };
          }
          return null;
        }]),
      ],
      // employed
      income_basic: [
        (<any> this.user.current.application.applicants[this.budgetFocus]).income_basic || 0,
        Validators.compose([
          Validators.min(0),
        ]),
      ],
      income_bonus: [
        (<any> this.user.current.application.applicants[this.budgetFocus]).income_bonus || 0,
        Validators.compose([
          Validators.min(0),
        ]),
      ],
      income_overtime: [
        (<any> this.user.current.application.applicants[this.budgetFocus]).income_overtime || 0,
        Validators.compose([
          Validators.min(0),
        ]),
      ],
      income_commission: [
        (<any> this.user.current.application.applicants[this.budgetFocus]).income_commission || 0,
        Validators.compose([
          Validators.min(0),
        ]),
      ],
      // self employed :: sole trader
      income_net_profit_latest: [
        (<any> this.user.current.application.applicants[this.budgetFocus]).income_net_profit_latest || 0,
        Validators.compose([
          Validators.min(0),
        ]),
      ],
      income_net_profit_previous: [
        (<any> this.user.current.application.applicants[this.budgetFocus]).income_net_profit_previous || 0,
        Validators.compose([
          Validators.min(0),
        ]),
      ],
      // self employed :: cmmpany director
      income_salary_latest: [
        (<any> this.user.current.application.applicants[this.budgetFocus]).income_salary_latest || 0,
        Validators.compose([
          Validators.min(0),
        ]),
      ],
      income_salary_previous: [
        (<any> this.user.current.application.applicants[this.budgetFocus]).income_salary_previous || 0,
        Validators.compose([
          Validators.min(0),
        ]),
      ],
      income_dividends_latest: [
        (<any> this.user.current.application.applicants[this.budgetFocus]).income_dividends_latest || 0,
        Validators.compose([
          Validators.min(0),
        ]),
      ],
      income_dividends_previous: [
        (<any> this.user.current.application.applicants[this.budgetFocus]).income_dividends_previous || 0,
        Validators.compose([
          Validators.min(0),
        ]),
      ],
      // account
      number_of_applicants: [
        this.user.current.application.applicants?.length || 1,
        Validators.compose([
          Validators.min(1),
          Validators.max(2),
        ]),
      ],
      savings: [
        this.user.current.application.deposit || 0,
        Validators.compose([
          Validators.min(0),
          Validators.required,
        ]),
      ],
      savings_include_stamp_duty: false,
      number_of_dependants: [
        this.user.current.application.dependants || 0,
        Validators.compose([
          Validators.min(0),
        ]),
      ],
      monthly_credit_commitments: [
        this.user.current.application.significant_monthly_outgoings || 0,
        Validators.compose([
          Validators.min(0),
        ]),
      ],
      total_credit_commitments: [
        (<any> this.user.current.application).total_credit_commitments || 0,
        Validators.compose([
          Validators.min(0),
        ]),
      ],
      term: [
        35,
        Validators.compose([
          Validators.min(0), Validators.max(40),
        ]),
      ],
      tenure: [
        'freehold',
        Validators.compose([
          Validators.required,
        ]),
      ],
      product: [
        '',
        Validators.compose([
          Validators.required,
        ]),
      ],
    });

  breakdownScenario: any | undefined;

  breakdownGroup: FormGroup = this.formBuilder
    .group({
      purchase_price: [
        0,
        Validators.compose([
          Validators.min(100000),
          Validators.max(1200000),
        ]),
      ],
      deposit: [
        0,
        Validators.compose([
          Validators.min(5000),
          Validators.max(200000),
        ]),
      ],
      tenure: [
        'freehold',
        Validators.compose([
          Validators.required,
        ]),
      ],
      product: [
        '',
        Validators.compose([
          Validators.required,
        ]),
      ],
    });

  repaymentStore: any;

  repaymentPart = false;

  // {
  //   product: P.LoanProduct;
  //   loan: number;
  // };
  repaymentGroup: FormGroup = this.formBuilder
    .group({
      growth: [
        5,
        Validators.compose([
          Validators.min(0),
          Validators.max(10),
        ]),
      ],
      year: [
        0,
        Validators.compose([
          Validators.min(0),
          Validators.max(4),
        ]),
      ],
      amount: [
        0,
        Validators.compose([
          Validators.min(0),
        ]),
      ],
    });

  budgetOptions = {
    ...defaultOptions,
  };

  depositOptions = {
    ...defaultOptions,
  };

  growthOptions = {
    ...defaultOptions,
    label: 'Annual growth',
    start: 5,
    range: {
      min: 0,
      max: 10,
    },
    step: 1,
    displayFormat: (v) => `${parseInt(v)}%`,
  };

  yearRepaymentOptions = {
    ...defaultOptions,
    label: 'Year of repayment',
    start: 4,
    range: {
      min: 0,
      max: 4,
    },
    step: 1,
    displayFormat: (v) => `Year ${parseInt(v || 0) + 1}`,
  };

  repaymentOptions = {
    ...defaultOptions,
    label: 'Annual growth',
    start: 0,
    range: {
      min: 0,
      max: 1000,
    },
    step: 100,
  };

  applicantsOptions = [
    { label: '1 Applicant', value: 1 },
    { label: '2 Applicants', value: 2 },
  ];

  partFullOptions = [
    { label: 'Full repayment', value: 'false' },
    { label: 'Part repayment', value: 'true' },
  ];

  yearPartOptions = [
    { label: 'End of year 1', value: 0 },
    { label: 'End of year 2', value: 1 },
    { label: 'End of year 3', value: 2 },
    { label: 'End of year 4', value: 3 },
  ];

  yearOptions = [
    ...this.yearPartOptions,
    { label: 'End of year 5', value: 4 },
  ];

  tenureOptions = [
    { label: 'Tenure: Freehold', value: 'freehold' },
    { label: 'Tenure: Leasehold', value: 'leasehold' },
  ];

  dependantOptions = [
    { label: 'Please select option', value: '' },
    { label: '0 dependants', value: 0 },
    { label: '1 dependant', value: 1 },
    { label: '2 dependants', value: 2 },
    { label: '3 dependants', value: 3 },
    { label: '4+ dependants', value: 4 },
  ];

  residencyOptions = [
    { label: 'Please select option', value: '' },
    { label: 'British passport', value: 'British passport' },
    { label: 'Indefinite leave to remain/ Settled Status', value: 'Indefinite leave to remain/ Settled Status' },
    { label: 'Lived in UK for more than 5 years', value: 'Lived in UK for more than 5 years' },
    { label: 'None of the above', value: 'None of the above' },
  ];

  employmentOptions = [
    { label: 'Please select option', value: '' },
    { label: 'Employed', value: 'Employed' },
    { label: 'Self-employed', value: 'Self-employed' },
  ];

  selfEmploymentOptions = [
    { label: 'Please select option', value: '' },
    { label: 'Sole trader', value: 'Sole trader' },
    { label: 'Company director (25%+ ownership)', value: 'Company director' },
  ];

  constructor(
    private user: UserService,
    private formBuilder: FormBuilder,
    private restApi: RestApiService,
    private currency: CurrencyPipe,
    private cdr: ChangeDetectorRef,
    private logger: LoggerService,
    iconRegistry: UIKitIconRegistry,
  ) {
    iconRegistry.registerIcons([
      calculatorIcon,
      profileIcon,
      infoIcon,
      chevronLeftIcon,
      startOverIcon,
      calendarIcon,
      gbpIcon,
      plusIcon,
      minusIcon,
      chevronRightIcon,
      chevronDownIcon,
      chevronUpIcon,
      homeIcon,
    ]);
  }

  productStore: P.LoanProduct[] = [];

  productOptions = [];

  working: boolean;

  instanceId: number;

  ngOnInit(): void {
    this.instanceId = Date.now();
    //
    this.working = true;
    this.restApi
      .getProducts({})
      .toPromise()
      .then((page) => {
        this.productStore = page.docs;
        this.productOptions = page.docs
          .map((product) => ({ label: product.name, value: product.id }));
      })
      .then(() => this.initBudgetStore())
      .then(() => this.budgetScenario?.budget && this.initBreakDownStore())
      .catch((error) => {
        if (error) {
          this.logger.report(error);
        }
        const defaultInterestRate = 0.0999;
        const defaultProduct = {
          id: '',
          name: `Proportunty Loan @${defaultInterestRate * 100}%`,
          interest_rate: defaultInterestRate,
          default: true,
          annual_early_repayment_charges: [5, 4, 3, 2, 1],
        } as P.LoanProduct;
        this.productStore = [
          defaultProduct,
        ];
        this.productOptions = [
          {
            value: defaultProduct.id,
            label: defaultProduct.name,
          },
        ];
      })
      .finally(() => {
        this.working = false;
        //
        if (this.listing) {
          //
          this.initRepaymentStore();
          //
          this.step = 'Breakdown';
          return;
        }
        //
        this.step = 'Budget';
      });
  }

  budgetFocusSet(index: 0 | 1): void {
    this.budgetFocus = index;

    const applicant = this.budgetStore.applicants[index] || {};

    this.budgetGroup
      .patchValue({
        residence_type: (<any>applicant).residence_type || '',
        employment_type: (<any>applicant).employment_type || '',
        self_employment_type: (<any>applicant).self_employment_type || '',
        dob: (<any>applicant).dob || '',
        income_basic: (<any>applicant).income_basic || 0,
        income_bonus: (<any>applicant).income_bonus || 0,
        income_overtime: (<any>applicant).income_overtime || 0,
        income_commission: (<any>applicant).income_commission || 0,
        income_net_profit_latest: (<any>applicant).income_net_profit_latest || 0,
        income_net_profit_previous: (<any>applicant).income_net_profit_previous || 0,
        income_salary_latest: (<any>applicant).income_salary_latest || 0,
        income_salary_previous: (<any>applicant).income_salary_previous || 0,
        income_dividends_latest: (<any>applicant).income_dividends_latest || 0,
        income_dividends_previous: (<any>applicant).income_dividends_previous || 0,
      }, { emitEvent: false });
  }

  budgetGroupToggle(event: MouseEvent | TouchEvent): void {
    event.stopImmediatePropagation();
    if (!this.listing) {
      this.initBudgetStore();
      this.step = 'Budget';
      this.event.emit(new Event(`${this.instanceId}-${this.step.toLocaleLowerCase()}`));
    }
  }

  budgetGroupSubmit(event: Event): void {
    if (!this.budgetScenario) {
      return event.preventDefault();
    }
    this.initBreakDownStore();
    this.step = 'Breakdown';
    this.event.emit(new Event(`${this.instanceId}-${this.step.toLocaleLowerCase()}`));
  }

  private initBudgetStore() {
    const applicants = (this.user.current.application.applicants || []);

    const product = this.productStore
      .find((product) => product.default)?.id
      || this.productStore[0];

    this.budgetFocus = 0;

    this.budgetStore = {
      applicants: applicants
        .map((applicant) => ({
          residence_type: (<any>applicant).residence_type || '',
          employment_type: (<any>applicant).employment_type || '',
          self_employment_type: (<any>applicant).self_employment_type || '',
          dob: (<any>applicant).dob || '',
          income_basic: (<any>applicant).income_basic || 0,
          income_bonus: (<any>applicant).income_bonus || 0,
          income_overtime: (<any>applicant).income_overtime || 0,
          income_commission: (<any>applicant).income_commission || 0,
          income_net_profit_latest: (<any>applicant).income_net_profit_latest || 0,
          income_net_profit_previous: (<any>applicant).income_net_profit_previous || 0,
          income_salary_latest: (<any>applicant).income_salary_latest || 0,
          income_salary_previous: (<any>applicant).income_salary_previous || 0,
          income_dividends_latest: (<any>applicant).income_dividends_latest || 0,
          income_dividends_previous: (<any>applicant).income_dividends_previous || 0,
        })),
      application: {
        number_of_applicants: applicants.length,
        term: this.getMaxTerm(applicants),
        tenure: 'freehold',
        savings: this.user.current.application.deposit || 0,
        savings_include_stamp_duty: false,
        number_of_dependants: this.user.current.application.dependants || 0,
        monthly_credit_commitments: this.user.current.application.significant_monthly_outgoings || 0,
        total_credit_commitments: (<any> this.user.current.application).total_credit_commitments || 0,
        product,
      },
    };

    this.budgetGroup.patchValue({
      ...this.budgetStore.applicants[this.budgetFocus],
      ...this.budgetStore.application,
    });

    this.budgetGroup.valueChanges
      .pipe(debounceTime(300))
      .pipe(startWith(this.budgetGroup.value))
      .subscribe(this.setBudgetStore.bind(this));

    return this.setBudgetStore();
  }

  private getMaxTerm(applicants: any[]): number {
    const today = moment();
    const hasDob = !!applicants
      .filter((applicant) => !!applicant.dob).length;
    const diff = 66 + applicants
      .map((applicant) => moment(applicant.dob))
      .reduce((oldest, current) => (oldest.isAfter(current)
        ? current
        : oldest), today)
      .diff(today, 'y');

    return hasDob
      ? Math.min(Math.max(diff, 1), 40)
      : 35;
  }

  private async setBudgetStore(value = this.budgetGroup.value) {
    const number_of_applicants = parseInt(value.number_of_applicants || 1);

    if (this.budgetStore.application.number_of_applicants === 2
      && number_of_applicants === 1) {
      this.budgetFocusSet(0);
    }

    this.budgetStore.applicants[this.budgetFocus] = {
      residence_type: value.residence_type || '',
      employment_type: value.employment_type || '',
      self_employment_type: value.self_employment_type || '',
      dob: value.dob || '',
      income_basic: parseInt(value.income_basic || 0),
      income_bonus: parseInt(value.income_bonus || 0),
      income_overtime: parseInt(value.income_overtime || 0),
      income_commission: parseInt(value.income_commission || 0),
      income_net_profit_latest: parseInt(value.income_net_profit_latest || 0),
      income_net_profit_previous: parseInt(value.income_net_profit_previous || 0),
      income_salary_latest: parseInt(value.income_salary_latest || 0),
      income_salary_previous: parseInt(value.income_salary_previous || 0),
      income_dividends_latest: parseInt(value.income_dividends_latest || 0),
      income_dividends_previous: parseInt(value.income_dividends_previous || 0),
    };

    const applicants = parseInt(value.number_of_applicants || 1) === 1
      ? [this.budgetStore.applicants[0]]
      : this.budgetStore.applicants;

    const tenure = value.tenure || 'freehold';

    this.budgetStore.application = {
      number_of_applicants,
      number_of_dependants: parseInt(value.number_of_dependants || 0),
      term: this.getMaxTerm(applicants),
      tenure,
      savings: parseInt(value.savings || 0),
      savings_include_stamp_duty: false,
      monthly_credit_commitments: parseFloat(value.monthly_credit_commitments || 0),
      total_credit_commitments: parseFloat(value.total_credit_commitments || 0),
      product: value.product,
    };

    // console.log(this.budgetStore);

    // for(const k of Object.keys(this.budgetGroup.controls)) {
    //   console.log(k, this.budgetGroup.controls[k].valid)
    // }

    // console.log(this.budgetGroup.valid);

    if (this.budgetGroup.valid) {
      let income = 0;
      let income_extra = 0;
      const deposit = parseInt(value.savings || 0);

      switch (value.employment_type) {
        case 'Employed':
          income = applicants
            .map((applicant) => parseInt(applicant.income_basic || 0))
            .reduce((total, part) => total + part, 0);
          income_extra = applicants
            .map((applicant) => parseInt(applicant.income_bonus || 0) + parseInt(applicant.income_overtime || 0)
              + parseInt(applicant.income_commission || 0))
            .reduce((total, part) => total + part, 0);
          break;
        case 'Self-employed':
          switch (value?.self_employment_type) {
            case 'Sole trader':
              income = applicants
                .map((applicant) => Math.min(
                  parseInt(applicant.income_net_profit_latest || 0),
                  parseInt(applicant.income_net_profit_previous || 0),
                ))
                .reduce((total, part) => total + part, 0);
              income_extra = 0;
              break;
            case 'Company director':
              income = applicants
                .map((applicant) => Math.min(
                  parseInt(applicant.income_salary_latest || 0) + parseInt(applicant.income_dividends_latest || 0),
                  parseInt(applicant.income_salary_previous || 0) + parseInt(applicant.income_dividends_previous || 0),
                ))
                .reduce((total, part) => total + part, 0);

              income_extra = 0;
              break;
            default:
              income = 0;
              income_extra = 0;
              break;
          }
          break;
        default:
          income = 0;
          income_extra = 0;
          break;
      }

      this.budgetStore.application = {
        ...this.budgetStore.application,
        income,
        income_extra,
        deposit,
      };

      if (income >= 30000
        || deposit < 5000) {
        const product = this.productStore
          .find((product) => product.id === value.product)
          || this.productStore[0];

        const interest_rate = product.interest_rate / 100;

        const output = await calculateBudget({
          case: 'WITH_INCOME_SAVINGS',
          income,
          income_extra,
          deposit,
          loan_percent: 0.25,
          interest_rate,
          tenure,
          advanced: <Calculator.InputAdvanced>{
            number_of_applicants: parseInt(value.number_of_applicants || 1),
            number_of_dependants: parseInt(value.number_of_dependants || 0),
            total_credit_commitments: parseFloat(value.total_credit_commitments || 0),
            monthly_credit_commitments: parseFloat(value.monthly_credit_commitments || 0),
          },
        });

        this.budgetScenario = output.with_proportunity;
        this.budgetScenario.budget = Math.floor(this.budgetScenario.budget / 1000) * 1000;

        if (this.budgetScenario.budget < 100000) {
          this.budgetScenario = void 0;
        }
      } else {
        this.budgetScenario = void 0;
      }
    } else {
      this.budgetScenario = void 0;
    }

    // console.log(this.budgetScenario);
  }

  breakdownGroupToggle(event: MouseEvent | TouchEvent): void {
    event.stopImmediatePropagation();
    if (this.budgetScenario?.budget) {
      this.initBreakDownStore();
      this.step = 'Breakdown';
      this.event.emit(new Event(`${this.instanceId}-${this.step.toLocaleLowerCase()}`));
    }
  }

  breakdownGroupSubmit(event: Event): void {
    event.preventDefault();
    this.initRepaymentStore();
    this.step = 'Repayment';
    this.event.emit(new Event(`${this.instanceId}-${this.step.toLocaleLowerCase()}`));
  }

  private initBreakDownStore() {
    const purchase_price = this.listing?.metadata?.price || this.budgetScenario.budget;

    const max = Math.max(purchase_price, this.user.budget);
    const min = Math.max(100000, purchase_price - (purchase_price * 0.1) - (this.user.current?.application?.deposit || 0));

    this.budgetOptions = {
      ...defaultOptions,
      label: 'Purchase Price',
      start: purchase_price,
      range: {
        min,
        max,
      },
      displayFormat: (v) => this.currency.transform(v, 'GBP', 'symbol', '1.0'),
    };

    const deposit = this.budgetStore.application.deposit
      || purchase_price * 0.05;

    this.depositOptions = {
      ...defaultOptions,
      label: 'Deposit (Min 5%)',
      start: deposit,
      range: {
        min: 0,
        max: deposit,
      },
      displayFormat: (v) => this.currency.transform(v, 'GBP', 'symbol', '1.0'),
    };

    const tenure = this.budgetStore.application.tenure || 'freehold';

    const product = this.budgetStore.application.product;

    this.breakdownGroup
      .patchValue({
        purchase_price,
        deposit,
        tenure,
        product,
      });

    this.breakdownGroup.valueChanges
      .pipe(startWith(this.breakdownGroup.value))
      .subscribe(this.setBreakdownStore.bind(this));

    return this.setBreakdownStore();
  }

  private async setBreakdownStore(value = this.breakdownGroup.value) {
    const purchase_price = parseInt(value.purchase_price || 0);

    const deposit = parseInt(value.deposit || 0);

    const tenure = value.tenure || 'freehold';

    const product = value.product;

    this.breakdownScenario = (await recommendation({
      purchase_price,
      deposit,
      tenure,
      income: this.budgetStore.application.income,
      income_extra: this.budgetStore.application.income_extra,
      term: this.budgetStore.application.term || 35,
      number_of_applicants: this.budgetStore.application.number_of_applicants,
      number_of_dependants: this.budgetStore.application.number_of_dependants,
      monthly_credit_commitment: this.budgetStore.application.monthly_credit_commitments,
      total_credit_commitments: this.budgetStore.application.total_credit_commitments,
    })) as unknown as Calculator.RecommendationOutput;

    // console.log(this.breakdownScenario);
  }

  repaymentGroupToggle(event: MouseEvent | TouchEvent): void {
    event.stopImmediatePropagation();
    if (this.budgetScenario?.budget
      && this.breakdownScenario?.recommendation) {
      this.initRepaymentStore();
      this.step = 'Repayment';
      this.event.emit(new Event(`${this.instanceId}-${this.step.toLocaleLowerCase()}`));
    }
  }

  repaymentGroupSubmit(event: Event): void {
    event.preventDefault();
    this.step = 'Repayment';
  }

  private initRepaymentStore(): void {
    const loan = this.breakdownScenario?.recommendation?.loan || 0;
    const loan_ltv = this.breakdownScenario?.recommendation?.loan_ltv || 0;
    const mortgage_per_month = this.breakdownScenario?.recommendation?.mortgage_per_month || 0;
    const loan_per_month = this.breakdownScenario?.recommendation?.loan_per_month || 0;
    const total_per_month = this.breakdownScenario?.recommendation?.total_per_month || 0;

    const purchase_price = this.breakdownScenario.input?.purchase_price || 0;

    const product = this.productStore
      .find((product) => product.id === this.breakdownGroup.value.product)
      || this.productStore[0];

    const amount = loan; //  Math.round(loan * 0.25);

    this.repaymentOptions = {
      ...this.repaymentOptions,
      label: 'Loan repayment',
      start: amount,
      range: { min: 1000, max: loan },
      step: 1000,
      displayFormat: (v) => this.currency.transform(v, 'GBP', 'symbol', '1.0'),
    };

    this.yearPartOptions = product.annual_early_repayment_charges
      .filter((v, i) => i < product.annual_early_repayment_charges.length - 1)
      .map((erc, i) => ({
        label: `End of year ${i + 1}`, value: i,
      }));

    this.yearOptions = [
      ...this.yearPartOptions,
      { label: `End of year ${product.annual_early_repayment_charges.length}`, value: product.annual_early_repayment_charges.length - 1 },

    ];
    // product.annual_early_repayment_charges
    //   .map((erc, i) => ({
    //     label: `End of ${i >= product.annual_early_repayment_charges.length - 1
    //       ? 'term'
    //       : `year ${i + 1}`}`, value: i
    //   }));

    this.yearRepaymentOptions = {
      ...this.yearRepaymentOptions,
      label: 'Repayment year',
      start: product.annual_early_repayment_charges.length - 1,
      range: { min: 0, max: product.annual_early_repayment_charges.length - 1 },
      step: 1,
    };

    this.repaymentStore = {
      product,
      loan,
      loan_ltv,
      purchase_price,
      mortgage_per_month,
      loan_per_month,
      total_per_month,
    };

    this.repaymentGroup
      .patchValue({
        amount,
        growth: 5,
        year: product.annual_early_repayment_charges.length - 1,
      });

    this.repaymentGroup.valueChanges
      .pipe(startWith(this.repaymentGroup.value))
      .subscribe(this.setRepaymentStore.bind(this));
  }

  private setRepaymentStore(value = this.repaymentGroup.value): void {
    const loan = this.repaymentStore.loan;
    const loan_ltv = this.repaymentStore.loan_ltv;
    const mortgage_per_month = this.repaymentStore.mortgage_per_month;
    const loan_per_month = this.repaymentStore.loan_per_month;
    const total_per_month = this.repaymentStore.total_per_month;
    const purchase_price = this.repaymentStore.purchase_price;
    const product = this.repaymentStore.product;
    const productRate = product.interest_rate / 100;

    const growth = parseInt(value.growth || 0) / 100;
    const principal = parseFloat(value.amount || 0);
    const remaining = loan - principal;
    const year = parseInt(value.year || 0);

    const ercRate = (product.annual_early_repayment_charges[year + 1] || 0) / 100;
    const erc = principal * ercRate;
    const discharge = principal === loan ? 100 : 0;

    const valuation_price = purchase_price * (1 + growth) ** (year + 1);

    const repayment_loan_ltv = principal / purchase_price;

    const loan_appreciation = (valuation_price * repayment_loan_ltv) - principal;

    const deposit_appreciation = (valuation_price * loan_ltv) - principal;

    const price_appreciation = valuation_price - purchase_price;

    const can_estimate_thereafter = !!remaining
      && !(year + 1 === product.annual_early_repayment_charges.length);

    const per_month_thereafter = ((loan - principal) * productRate) / 12;
    const per_month_thereafter_delta = loan_per_month - per_month_thereafter;
    const per_month_total_thereafter = per_month_thereafter + mortgage_per_month;
    const per_month_total_thereafter_delta = total_per_month - per_month_total_thereafter;

    const total = Math.max(principal + erc + loan_appreciation + discharge, principal + erc + discharge);

    this.repaymentStore = {
      ...this.repaymentStore,
      ...value,
      principal,
      remaining,
      year,
      ercRate,
      erc,
      discharge,
      price_appreciation,
      loan_appreciation,
      deposit_appreciation,
      total,
      per_month_thereafter,
      per_month_thereafter_delta,
      per_month_total_thereafter,
      per_month_total_thereafter_delta,
      valuation_price,
      can_estimate_thereafter,
    };
  }

  togglePart(event: any): void {
    // console.log(event);
    this.repaymentPart = event?.value === 'true';
    // console.log(this.repaymentPart);

    this.repaymentGroup.patchValue({
      amount: this.repaymentStore?.loan || 0,
      year: this.repaymentPart
        ? this.yearOptions.length - 1
        : this.yearPartOptions.length - 1,
    });
  }

  previousStep(event?: MouseEvent | TouchEvent): void {
    if (event) {
      event.stopImmediatePropagation();
    }
    switch (this.step) {
      case 'Repayment':
        this.repaymentGroup.reset();
        this.step = 'Breakdown';
        break;
      case 'Breakdown':
        this.breakdownGroup.reset();
        this.step = 'Budget';
        break;
      default:
        break;
    }

    this.event.emit(new Event(`${this.instanceId}-${this.step.toLocaleLowerCase()}`));
  }

  resetStep(event?: MouseEvent | TouchEvent): void {
    if (event) {
      event.stopImmediatePropagation();
    }

    this.budgetGroup.reset();
    this.breakdownGroup.reset();
    this.repaymentGroup.reset();
    this.step = 'Budget';
  }
}
